/**
 * Putting a fabric onto a garment.
 *
 * Phase 2 of docs/decisions/fabric-picker-and-gating.md. Two behaviours, and
 * which one you get depends on the layer you have selected:
 *
 *   **Nothing special selected** → a new layer, filled across all 18 face
 *   regions. The ordinary "make this shirt denim".
 *
 *   **The selected layer has Paint inside only** → the fabric lands *in that
 *   layer*, only where it already has pixels. That is "apply it over what I
 *   drew so it fits the shape", done with `source-atop`, which is the same
 *   composite the brush already uses for the same reason.
 *
 * Filled per region rather than as one canvas-wide rect, because the template is
 * a UV atlas: the 54.9% between the regions is dead space that no part of the
 * body samples, and painting it costs bytes in the export for pixels nobody can
 * ever see.
 */
import { CANVAS, type RegionId } from "../../shared/format";
import { REGIONS } from "../../shared/regions";
import { type FabricSource, recordFabric } from "./fabric-source";
import type { LayerStack, RuntimeLayer } from "./layers";
import type { DirtyRect } from "./paint";
import { phaseFor, phaseYFor, tileFor } from "./seam";

/**
 * How wide one tile is drawn, in template pixels.
 *
 * **Template pixels, not a multiplier.** The tiles are not all the same size —
 * one fabric is 169x256 — so a shared multiplier would draw a different-sized
 * weave for each, and the number in the UI would mean nothing. A width in
 * template pixels is the same promise for every fabric, and it is what a
 * designer actually thinks in: the torso front panel is about 128px, so 90 is a
 * little under one repeat across a chest.
 */
export const DEFAULT_TILE_PX = 90;

/** The range worth offering: a dense weave, up to one repeat per panel. */
export const MIN_TILE_PX = 16;
export const MAX_TILE_PX = 256;

/**
 * The tile as a repeating fill, scaled so it is `tilePx` wide.
 *
 * Shared by the picker and the fill so a swatch and its result are the same
 * code rather than the same intention. Returns null when the context refuses a
 * pattern, which happens for a zero-sized canvas.
 */
/**
 * The pattern transform that makes a tile exactly `tilePx` wide.
 *
 * Pure and separate because it is the promise the UI makes. The tiles are not
 * all the same size — one fabric is 169x256 — so this has to normalise, or
 * "90 px weave" would mean 90px for most fabrics and something else for the
 * others, and the slider would be lying about the only thing it says.
 */
export function tileScale(tilePx: number, naturalWidth: number, dpr = 1): number {
  if (!(naturalWidth > 0)) return 0;
  return (tilePx / naturalWidth) * dpr;
}

export function tilePattern(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource & { naturalWidth?: number; width?: number },
  tilePx: number,
  dpr = 1,
): CanvasPattern | null {
  const pattern = ctx.createPattern(img as CanvasImageSource, "repeat");
  if (!pattern) return null;
  const natural = img.naturalWidth || (typeof img.width === "number" ? img.width : 0);
  const scale = tileScale(tilePx, natural, dpr);
  if (!scale) return null;
  // The pattern is transformed rather than the context, so fill rects stay in
  // their own coordinates and the tile still starts at the origin — which is
  // what keeps the phase identical across every region.
  pattern.setTransform(new DOMMatrix([scale, 0, 0, scale, 0, 0]));
  return pattern;
}

/**
 * Roblox leaves a 2px unpainted gutter between every pair of adjacent regions,
 * and its own docs say to paint into it so clothes appear without a seam.
 * Filling exactly to the rect leaves a skin-coloured hairline at every fold.
 */
export const BLEED = 3;

/** A region grown by `bleed` and clamped to the canvas. */
export function bleedRect(rect: DirtyRect, bleed = BLEED): DirtyRect {
  const x = Math.max(0, rect.x - bleed);
  const y = Math.max(0, rect.y - bleed);
  const right = Math.min(CANVAS.width, rect.x + rect.w + bleed);
  const bottom = Math.min(CANVAS.height, rect.y + rect.h + bleed);
  return { x, y, w: Math.max(0, right - x), h: Math.max(0, bottom - y) };
}

export interface FabricFillOptions {
  /** Tile width in template pixels. */
  tilePx?: number;
  /** Hex. When set, the grain variant is used and this becomes the colour. */
  tint?: string | null;
  /**
   * A fabric layer this one supersedes.
   *
   * Trying fabrics on is a loop — pick, look at the body, pick another — and
   * without this each pass leaves a layer behind, so choosing between five
   * fabrics ends with four you have to find and delete. Removed inside the same
   * transaction, so the whole swap is one undo step back to the previous
   * fabric rather than two.
   */
  replaceLayerId?: string | null;
  /**
   * Reports the pixels either side, for undo.
   *
   * Only fires for the in-place case. Adding a layer is a *structure* change and
   * `stack.transaction` already records it — undo removes the whole layer, which
   * is both correct and free. Filling an existing layer changes no structure at
   * all, so without this it would be silently un-undoable.
   */
  onEdit?: (layerId: string, rect: DirtyRect, before: ImageData, after: ImageData) => void;
}

/**
 * Which half of the library a fabric is drawn from.
 *
 * `colour` is the albedo, used when the fabric keeps its own colour. `grain` is
 * the luminance variant, normalised to mid-grey, used when the *user* picks the
 * colour — it carries the weave and none of the hue, which is the only way to
 * put a red shirt in leather without the leather making it brown.
 */
export type TileKind = "colour" | "grain";

const cache = new Map<string, Promise<HTMLImageElement>>();

/** The tile, decoded once per id and kind. */
export function loadTile(id: string, kind: TileKind = "colour"): Promise<HTMLImageElement> {
  const key = `${kind}/${id}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const p = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", () => reject(new Error(`fabric tile ${key}`)));
    img.src = `/textures/${kind}/${id}.jpg`;
  });
  cache.set(key, p);
  return p;
}

/**
 * Paint the tile across every face region.
 *
 * With a tint, the base colour goes down first and the grain goes over it with
 * `overlay` — **not** `multiply`. The grain is centred on mid-grey, and
 * multiplying by mid-grey halves the brightness, so a multiply would turn every
 * colour into a muddy half-strength version of itself. Overlay treats mid-grey
 * as identity: the weave darkens where the fabric is dark, lightens where it is
 * light, and leaves the chosen colour exactly as chosen everywhere between.
 */
function paintRegions(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  tilePx: number,
  tint?: string | null,
) {
  const ids = Object.keys(REGIONS) as RegionId[];
  const rects = new Map(ids.map((id) => [id, bleedRect(REGIONS[id])]));

  // The base colour first, across everything, so the overlay below has
  // something to sit on. It has no phase of its own — it is flat.
  if (tint) {
    ctx.fillStyle = tint;
    for (const r of rects.values()) {
      if (r.w > 0 && r.h > 0) ctx.fillRect(r.x, r.y, r.w, r.h);
    }
  }

  /*
    The weave is built on its own layer and composited ONCE.

    It used to be drawn region by region straight onto the tint with `overlay`
    already set — and `bleedRect` grows every region by BLEED, so adjacent
    regions overlap by `2 * BLEED - GUTTER_PX` = 4 texels. Every texel in that
    overlap took the overlay TWICE.

    Measured with a flat 0x40 grain over a purple tint: the interior reads
    luminance 44 and a four-texel band at every boundary reads 22 — exactly
    half. Horizontally and vertically, at all eighteen faces, which is a dark
    grid over the whole garment.

    That grid is baked into the artwork, so it survives export untouched. It
    reads as a seam and is not one: the edge pipeline was doing its job the
    whole time, and no amount of seam fixing could have removed it.

    Drawing every region with `source-over` onto a scratch first means the
    overlaps simply overwrite — the second region's weave replaces the first's
    rather than multiplying with it — and the finished weave is composited in
    one pass, so every texel gets exactly one overlay.
  */
  const weave = weaveScratch();
  const wctx = weave.getContext("2d");
  if (!wctx) return;
  wctx.clearRect(0, 0, CANVAS.width, CANVAS.height);

  /*
    One pattern per region, not one for the canvas.

    Each face gets the tile size that closes around *its* strip and the phase
    that puts it where it belongs on the body — see seam.ts. Drawing the whole
    canvas with a single pattern is what makes a check drift by 2px at every
    gutter and land mid-motif at the wrap.
  */
  for (const id of ids) {
    const r = rects.get(id);
    if (!r || r.w <= 0 || r.h <= 0) continue;
    const pattern = tilePattern(wctx, img, tileFor(id, tilePx));
    if (!pattern) continue;
    wctx.save();
    wctx.beginPath();
    wctx.rect(r.x, r.y, r.w, r.h);
    wctx.clip();
    /*
      Translating the context rather than the pattern: the pattern transform is
      already carrying the scale, and composing the two by hand is how a sign
      error gets in.

      And one did. The contract is that a texel's pattern coordinate equals its
      position AROUND THE BODY: `patternX = templateX - phaseFor(id)`, which is
      what removes the 2px gutter the atlas inserts at every fold. A canvas
      pattern repeats from the current transform's origin, so `translate(t)`
      makes the coordinate at screen x equal `x - t` — meaning t must be
      `+phase`, and the rect being filled has to be expressed in that same
      translated space as `r.x - phase`.

      It read `translate(-phase)` with `fillRect(r.x + phase)`, which yields
      `x + phase`. That does not merely fail to remove the gutter, it applies it
      backwards: the error is `2 * phase`, and since phase grows by exactly 2 per
      face around a strip, the pattern drifted by 4 texels per fold — twice the
      defect this whole file exists to remove.

      Measured with a ramp tile whose value encodes position, sampled well
      inside each face to stay clear of the bleed overlap: the four torso faces
      sat 54.2, 51.2, 46.7 and 42.2 texels from where the body says they should,
      a drift of 12 texels around one turn. Now 1.5, 0, 0, 0.

      No test guards this, and that is not an oversight: the arithmetic in
      seam.ts was correct throughout and its tests passed the whole time. The
      bug was in applying it to a canvas transform, and the suite has no canvas.
      The sign is spelled out above so the next reader can check it by eye.
    */
    const phase = phaseFor(id);
    /*
      And the same correction downward.

      Horizontal drift has been removed since seam.ts was written; vertical
      never was, so a strip's `up` cap and its side row sat 2px out of phase and
      every cap-to-side fold showed a line while side-to-side folds came out
      clean. Same arithmetic, other axis.
    */
    const phaseY = phaseYFor(id);
    wctx.translate(phase, phaseY);
    wctx.fillStyle = pattern;
    wctx.fillRect(r.x - phase, r.y - phaseY, r.w, r.h);
    wctx.restore();
  }

  // One pass. `overlay` only when there is a tint under it to modulate;
  // without one the weave IS the artwork and simply lands.
  ctx.globalCompositeOperation = tint ? "overlay" : "source-over";
  ctx.drawImage(weave, 0, 0);
  ctx.globalCompositeOperation = "source-over";
}

/**
 * Repaint a layer that is already a fabric, with the fabric changed.
 *
 * The re-fill behind the colour dot. Deliberately not `applyFabric` with a
 * replaceLayerId: that removes the layer and adds a new one, which is a
 * *structure* change, and a colour picker streaming updates through a drag
 * would push one of those per frame and bury every earlier edit under a
 * hundred entries.
 *
 * This writes pixels through `putPixels`, which composites and bumps `rev` and
 * records nothing — so the caller can repaint freely while a colour is being
 * dragged and push a single history entry when it settles. The same shape the
 * brush already uses, where a stroke previews freely and commits once.
 *
 * Returns false when the layer is gone or the tile will not load; the caller
 * decides whether that is worth saying out loud.
 */
export async function refillLayer(
  stack: LayerStack,
  layerId: string,
  next: FabricSource,
): Promise<boolean> {
  const layer = stack.get(layerId);
  if (!layer) return false;

  const img = await loadTile(next.id, next.tint ? "grain" : "colour");
  const flat = renderFabric(img, next.tilePx, next.tint);
  if (!flat) return false;
  const scratch = flat.getContext("2d");
  if (!scratch) return false;

  // Still here? A drag can outlive its layer if the user deletes it mid-pick.
  if (!stack.get(layerId)) return false;

  const full: DirtyRect = { x: 0, y: 0, w: CANVAS.width, h: CANVAS.height };
  // putImageData replaces rather than draws over, alpha included, which is what
  // a whole-layer re-fill wants: the old fabric leaves completely.
  stack.putPixels(layerId, full, scratch.getImageData(0, 0, full.w, full.h));
  // After the write, so it carries the rev these pixels actually have.
  layer.fabric = { ...next, rev: layer.rev };
  return true;
}

export interface FabricApplied {
  layer: RuntimeLayer;
  /** True when it landed inside an existing layer rather than on a new one. */
  inPlace: boolean;
}

export async function applyFabric(
  stack: LayerStack,
  fabric: { id: string; name: string },
  opts: FabricFillOptions = {},
): Promise<FabricApplied | null> {
  const tilePx = opts.tilePx ?? DEFAULT_TILE_PX;
  const tint = opts.tint ?? null;
  const img = await loadTile(fabric.id, tint ? "grain" : "colour");

  const active = stack.active();
  // Only artwork can take a fill in place: a placement's canvas is the source
  // image at its own size and angle, so filling it would paint the logo itself.
  const inPlace = Boolean(active && active.kind === "artwork" && active.paintInsideOnly);

  let result: FabricApplied | null = null;

  const replacing = !inPlace && opts.replaceLayerId ? opts.replaceLayerId : null;
  const label = inPlace
    ? `Fill with ${fabric.name}`
    : replacing
      ? `Change to ${fabric.name}`
      : `Add ${fabric.name}`;

  stack.transaction(label, () => {
    // Removed before the new one is added, so the replacement lands where the
    // old fabric was rather than on top of it.
    if (replacing) stack.remove(replacing);
    const layer = inPlace && active ? active : stack.addArtwork({ name: fabric.name });
    const ctx = layer.source.getContext("2d");
    if (!ctx) return;

    const full: DirtyRect = { x: 0, y: 0, w: CANVAS.width, h: CANVAS.height };
    const before = inPlace && opts.onEdit ? ctx.getImageData(0, 0, full.w, full.h) : null;

    ctx.save();
    /*
      Composed into a scratch canvas first, then drawn on in one go.

      The tint needs `overlay` between the colour and the grain, and the
      in-place case needs `source-atop` against the layer. Those are two
      different composites wanting the same context, and interleaving them gives
      neither. One flattened image, drawn once, keeps the two concerns apart.
    */
    const flat = renderFabric(img, tilePx, tint);
    // source-atop keeps the fabric inside what is already drawn. The same
    // composite the brush uses for Paint inside only, for the same reason —
    // and never source-in or copy, which would clear everything outside it.
    if (inPlace) ctx.globalCompositeOperation = "source-atop";
    if (flat) ctx.drawImage(flat, 0, 0);
    ctx.restore();

    if (before && opts.onEdit) {
      opts.onEdit(layer.id, full, before, ctx.getImageData(0, 0, full.w, full.h));
    }
    // The stack cannot see a write that went straight to a layer's canvas.
    stack.markPainted(layer.id);
    /*
      Remember what this was made of — but only when the fill owns the whole
      layer.

      An in-place fill lands with `source-atop` over art the user drew, so the
      layer is their shape wearing this fabric. Re-filling it later would be
      lossless for the shape and destructive for everything the shape used to
      contain, and more to the point the layer is not "a fabric" — it is a
      drawing. Recording a source here would offer a colour dot that quietly
      throws work away.

      Read after markPainted(), never before: that call bumps `rev`, and a
      record holding the pre-bump value would read as already-painted-over and
      expire the instant it was written.
    */
    if (!inPlace) {
      layer.fabric = recordFabric(fabric, tilePx, tint, layer.rev);
    }
    result = { layer, inPlace };
  });

  stack.compose();
  return result;
}

/**
 * A full-canvas what-if, for `stack.setPreview`.
 *
 * One scratch canvas, reused. A hover can fire many times a second and a fresh
 * 585x559 buffer is 1.3 MB each — allocating one per sample is the kind of
 * garbage that shows up as stutter on exactly the low-end hardware this audience
 * uses.
 *
 * The regions are painted the same way the real fill paints them, so what the
 * body shows during a hover is what lands when you commit.
 */
let previewScratch: HTMLCanvasElement | null = null;
let fillScratch: HTMLCanvasElement | null = null;

/**
 * Where the weave is assembled before it is composited once.
 *
 * Module-level and reused: `refillLayer` runs on every frame of a colour drag,
 * and allocating a 585x559 canvas per frame is the kind of cost that only shows
 * up as jank on the slowest device somebody owns.
 */
let weaveCanvas: HTMLCanvasElement | null = null;

function weaveScratch(): HTMLCanvasElement {
  if (weaveCanvas) return weaveCanvas;
  const c = document.createElement("canvas");
  c.width = CANVAS.width;
  c.height = CANVAS.height;
  weaveCanvas = c;
  return c;
}

function scratchFor(which: "preview" | "fill"): HTMLCanvasElement {
  const existing = which === "preview" ? previewScratch : fillScratch;
  if (existing) return existing;
  const c = document.createElement("canvas");
  c.width = CANVAS.width;
  c.height = CANVAS.height;
  if (which === "preview") previewScratch = c;
  else fillScratch = c;
  return c;
}

/**
 * The fabric across all regions, flattened.
 *
 * Two scratch canvases rather than one: a preview can be on screen at the exact
 * moment a fill is composed, and sharing a buffer between them would paint the
 * body with whatever the fill was halfway through building.
 */
export function renderFabric(
  img: HTMLImageElement,
  tilePx: number,
  tint?: string | null,
  which: "preview" | "fill" = "fill",
): HTMLCanvasElement | null {
  const canvas = scratchFor(which);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, CANVAS.width, CANVAS.height);
  paintRegions(ctx, img, tilePx, tint);
  return canvas;
}

export function previewFor(
  img: HTMLImageElement,
  tilePx: number,
  tint?: string | null,
): HTMLCanvasElement | null {
  return renderFabric(img, tilePx, tint, "preview");
}
